import { QuickSelection, QuickSelectionType } from "@/types/tab";
import "./index.scss";
import { useCallback, useMemo } from "react";
import { useTracker } from "@/context/TrackerContext";
import Exposure from "@/components/exposure";

/**
 * 快筛栏骨架屏
 * @returns 
 */
const QuickSelectBarSkeleton = () => {
  return (
    <div className="quick-select-bar-skeleton">
      <div className="quick-select-item skeleton-item"></div>
      <div className="quick-select-item skeleton-item"></div>
      <div className="quick-select-item skeleton-item"></div>
    </div>
  );
};

interface QuickSelectItemProps {
  type: QuickSelectionType;
  index: number;
  quickSelection: QuickSelection;
  isActive: boolean;
  couponIconUrl?: string; // 券快筛图标
  lxConfig: Record<string, any>;
  onQuickSelectionClick: (selection: QuickSelection) => void;
}
/**
 * 快筛项
 * @param props 
 * @returns 
 */
const QuickSelectItem: React.FC<QuickSelectItemProps> = function (props) {
  const { type, index, quickSelection, isActive, couponIconUrl, lxConfig, onQuickSelectionClick } = props || {};
  const tracker = useTracker();

  // 埋点上报自定义参数
  const lxCustom = useMemo(() => ({
    coupon_id: type === QuickSelectionType.Coupon ? quickSelection.id : "",
    filter_type: type === QuickSelectionType.Coupon ? "red" : "ordinary",
    filter_index: index,
    filter_name: quickSelection.name,
  }), [type, index, quickSelection]);

  // 曝光埋点
  const handleExposure = useCallback(() => {
    if (tracker && lxConfig?.TABBAR_BID_MV?.QUICK_SELECTION) {
      tracker.moduleView(lxConfig.TABBAR_BID_MV.QUICK_SELECTION, lxCustom);
    }
  }, [tracker, lxCustom]);

  // 点击快筛
  const handleClick = useCallback(() => {
    // 调用回调
    onQuickSelectionClick(quickSelection)
    // 上报埋点
    if (tracker && lxConfig?.TABBAR_BID_MC?.QUICK_SELECTION) {
      tracker.moduleClick(lxConfig.TABBAR_BID_MC.QUICK_SELECTION, lxCustom);
    }
  }, [tracker, lxCustom, onQuickSelectionClick, quickSelection]);

  return (
    <Exposure onExpose={handleExposure}>
      <div
        key={quickSelection.id}
        className={`quick-select-item
          ${type === QuickSelectionType.Coupon ? "is-coupon" : ""}
          ${isActive ? "is-active" : ""}
        `}
        onClick={handleClick}
      >
        {/* 券快筛 Icon */}
        {type === QuickSelectionType.Coupon ? (
          <img
            className="quick-select-item-icon"
            src={couponIconUrl || "https://p0.meituan.net/pepperfe/ba05cb39f736e80a3873b926609d444f4797.png"}
          />
        ) : null}
        <span className="quick-select-item-label">{quickSelection.name}</span>
      </div>
    </Exposure>
  );
};

interface QuickSelectBarProps {
  isInitFinished: boolean; // 是否初始化完成，未初始化完成展示骨架屏
  canShowCouponSelection: boolean; // 是否可以展示券快筛，仅外卖场景展示
  useCouponSelection: QuickSelection | null; // 用户点击红包墙去使用按钮，更新展示的券快筛
  couponSelectionList: QuickSelection[]; // 接口返回的券快筛列表
  quickSelectionList: QuickSelection[]; // 接口返回的普通快筛列表
  activeQuickSelection: QuickSelection | null; // 当前选中的普通快筛
  activeCouponSelection: QuickSelection | null; // 当前选中的券快筛
  couponIconUrl?: string; // 券快筛图标
  lxConfig: Record<string, any>; // 埋点配置
  onQuickSelectionClick: (selection: QuickSelection) => void; // 点击普通快筛回调
  onCouponSelectionClick: (selection: QuickSelection) => void; // 点击券快筛回调
}
/**
 * 快筛栏
 * @param props 
 * @returns 
 */
const QuickSelectBar: React.FC<QuickSelectBarProps> = function (props) {
  const {
    isInitFinished,
    canShowCouponSelection,
    useCouponSelection,
    couponSelectionList = [],
    quickSelectionList = [],
    activeQuickSelection,
    activeCouponSelection,
    couponIconUrl, // 券快筛图标
    lxConfig,
    onQuickSelectionClick,
    onCouponSelectionClick,
  } = props || {};

  // 计算展示的券快筛列表
  const displayCouponSelectionList = useMemo((): QuickSelection[] => {
    // 1. 非外卖Tab场景，不展示券快筛
    if (!canShowCouponSelection) return [];

    // 2. 用户未点击红包墙的去使用，使用默认的券快筛列表，只展示前两个
    if (!useCouponSelection) return couponSelectionList.slice(0, 2);

    // 3. 用户点击了红包墙的去使用
    //   1) couponSelectionList 中存在用户点击的券，不变更
    //   2) couponSelectionList 中不存在用户点击的券，将第一个券快筛换成用户点击的券快筛
    const isSelectCouponInList = couponSelectionList.some(
      (item) => item?.id === useCouponSelection?.id
    );
    return isSelectCouponInList
      ? couponSelectionList
      : [useCouponSelection, couponSelectionList?.[1]];
  }, [canShowCouponSelection, useCouponSelection, couponSelectionList]);

  if (!isInitFinished) {
    // 初始化未完成，展示骨架屏
    return <QuickSelectBarSkeleton />;
  } else if (!displayCouponSelectionList?.length && !quickSelectionList?.length) {
    // 无券快筛，无普通快筛，不展示快筛栏
    return null;
  }
  return (
    <div className="quick-select-bar">
      {/* 券快筛 */}
      {displayCouponSelectionList.length
        ? displayCouponSelectionList.map((item, index) => (
            <QuickSelectItem
              key={item.id}
              index={index + 1}
              type={QuickSelectionType.Coupon}
              quickSelection={item}
              couponIconUrl={couponIconUrl} // 券快筛图标
              isActive={item.id === activeCouponSelection?.id}
              lxConfig={lxConfig}
              onQuickSelectionClick={onCouponSelectionClick}
            />
          ))
        : null}
      {/* 普通快筛 */}
      {quickSelectionList.length
        ? quickSelectionList.map((item, index) => (
            <QuickSelectItem
              key={item.id}
              index={index + 1}
              type={QuickSelectionType.Common}
              quickSelection={item}
              isActive={item.id === activeQuickSelection?.id}
              lxConfig={lxConfig}
              onQuickSelectionClick={onQuickSelectionClick}
            />
          ))
        : null}
    </div>
  );
};

export default QuickSelectBar;
